import { createKernel, createSignalRRealtimeClient, type Kernel } from "@crialume/core";
import { layoutService } from "../services/layout-service";
import { uiService } from "../services/ui-service";
import { createAuthAdapter, createTokenStorage } from "./auth";
import { resolveHubUrl, type AppConfig } from "./config";
import { manifests } from "./modules";
import { readTheme, THEME_KEY } from "./theme";

export function createAppKernel(config: AppConfig): Kernel<AppConfig> {
  const kernel = createKernel({
    config,
    manifests,
    authAdapter: createAuthAdapter(config),
    tokenStorage: createTokenStorage(config),
    ui: uiService,
    layout: layoutService,
    initialState: { theme: readTheme() },
    logLevel: import.meta.env.DEV ? "debug" : "warn",
    // Tempo real (hub /hubs/app). Sem backend (auth mock) ou desligado: cliente
    // local sem rede, com status "disconnected" (telas caem para polling).
    ...(config.realtime.enabled && config.auth.mode === "jwt"
      ? {
          createRealtime: (deps) =>
            createSignalRRealtimeClient({ url: resolveHubUrl(config), ...deps }),
        }
      : {}),
  });

  // Roles mudaram no servidor: o token renovado traz as permissões atuais (menu e botões
  // se ajustam pela sessão). Vale para a empresa toda; quem não foi afetado recebe as mesmas.
  kernel.realtime.on("access.changed", () => kernel.auth.refreshAccess());

  // O shell é o único que ESCREVE no estado compartilhado; aplica o tema no DOM.
  const applyTheme = () => {
    const { theme } = kernel.store.getState();
    document.documentElement.dataset["theme"] = theme;
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* Theme still works without persistence. */
    }
  };
  applyTheme();
  kernel.store.subscribe((state, previous) => {
    if (state.theme !== previous.theme) applyTheme();
  });

  if (import.meta.env.DEV) {
    // Diagnóstico no console: __crialume.registry.snapshot()
    (window as unknown as { __crialume: Kernel }).__crialume = kernel;
  }

  return kernel;
}
